import React, { useState, useEffect } from 'react';
import { getAllWallets, getHouseInfo } from '../services/api';
import WalletGrid from '../components/WalletGrid';
import { formatBTC } from '../utils/format';

function WalletVault() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [network, setNetwork] = useState('mainnet');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch house info to get network
      const houseInfo = await getHouseInfo();
      if (houseInfo && houseInfo.network) {
        setNetwork(houseInfo.network);
      }
      
      // Fetch wallets with balances
      const walletsData = await getAllWallets(true);
      setWallets(walletsData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load wallet vault');
      console.error('Wallet vault error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && wallets.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading wallet vault...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="bg-zinc-900 border border-red-600 rounded-lg p-6 max-w-md">
          <h3 className="text-red-400 font-bold text-lg mb-2">Error</h3>
          <p className="text-gray-300">{error}</p>
          <button onClick={loadData} className="mt-4 bg-orange-500 text-black px-4 py-2 rounded hover:bg-orange-400 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  // Calculate total balance
  const totalBalance = wallets.reduce((sum, wallet) => sum + (wallet.balance_sats || 0), 0);
  const totalBalanceBTC = totalBalance / 100000000;

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-zinc-900 border-b border-zinc-800 shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">💼 Wallet Vault</h1>
              <p className="text-gray-400 mt-1">
                Manage your Bitcoin wallet vault
                {network === 'testnet' && <span className="ml-2 text-xs bg-orange-900/50 text-orange-400 px-2 py-1 rounded">TESTNET</span>}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Total Vault Balance</p>
              <p className="text-2xl font-bold text-orange-400">{formatBTC(totalBalanceBTC)} BTC</p>
            </div>
            <button onClick={loadData} className="bg-orange-500 text-black px-4 py-2 rounded-lg hover:bg-orange-400 flex items-center gap-2 font-medium">
              <span>🔄</span> Refresh
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <WalletGrid wallets={wallets} onUpdate={loadData} />
      </div>
    </div>
  );
}

export default WalletVault;
